import { useCallback, useEffect, useState } from "react";
import { Avatar, Empty, Spin } from "antd";
import dayjs from "dayjs";
import { getNotificationReadLogs, NotificationReadLogItem } from "../../../api/NotificationReadLog";

interface Props {
    customerId?: number;
}

// Viết tắt tên người dùng để hiển thị trên avatar (vd: "truc page admin" -> "TP")
const getInitials = (name?: string) => {
    if (!name) return "HT";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "HT";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export default function HistoryList({ customerId }: Props) {
    const [items, setItems] = useState<NotificationReadLogItem[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchLogs = useCallback(async () => {
        if (!customerId) {
            setItems([]);
            return;
        }
        setLoading(true);
        const res = await getNotificationReadLogs({ customer_id: customerId, limit: 50 });
        if (res.status && res.data) {
            setItems(res.data.items);
        } else {
            setItems([]);
        }
        setLoading(false);
    }, [customerId]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    if (loading) {
        return (
            <div className="flex justify-center py-10">
                <Spin />
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div className="py-10">
                <Empty description="Chưa có lịch sử đọc thông báo" />
            </div>
        );
    }

    return (
        <div className="relative px-4 py-4">

            {/* LINE */}
            <div className="absolute left-4 top-0 bottom-0 w-[2px] bg-gray-200" />

            {items.map((item) => {
                // Người tạo thông báo (hiển thị ở header). Người đọc (user) hiển thị ở dòng cuối.
                const creator = item.notification?.creator;
                const creatorName = creator?.fullname || "Hệ thống";
                const time = item.created_at ? dayjs(item.created_at).format("DD/MM/YYYY HH:mm:ss") : "";
                const title = item.notification?.title || "";
                const content = item.notification?.content || "";

                return (
                    <div key={item.id} className="relative mb-6">

                        {/* DOT */}
                        <div className="absolute left-[7px] top-3 -translate-x-1/2 w-3 h-3 bg-white rounded-full border-4 border-blue-500" />

                        {/* ROW */}
                        <div className="flex gap-3 ml-4">
                            {/* CONTENT */}
                            <div className="flex-1 bg-gray-50 rounded-lg p-3 dark:bg-[#1f1f1f]">

                                {/* HEADER - Người tạo thông báo */}
                                <div className="flex items-center gap-2 mb-1">
                                    <Avatar size={24} style={{ background: "#3b82f6" }}>
                                        {getInitials(creator?.fullname)}
                                    </Avatar>

                                    <span className="text-sm font-medium text-000000e0">{creatorName}</span>

                                    {/* 👉 TIME */}
                                    <span className="text-xs text-gray-400 ml-2">
                                        {time}
                                    </span>
                                </div>

                                {/* TITLE */}
                                {title && (
                                    <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                        {title}
                                    </div>
                                )}

                                {/* CONTENT (đã bao gồm dòng "[Người đọc: ...]" từ API) */}
                                {content && (
                                    <div className="text-sm text-gray-700 dark:text-gray-300 mt-1 whitespace-pre-line">
                                        {content}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
